import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { SmeDocumentsApi } from '../tokens/sme-documents.token';
import { DocumentVaultRow, DocumentUploadResult } from '../models/sme-document.model';

/**
 * REST-only implementation of `SmeDocumentsApi` (Story E / EM-472, PR-03 / §10.8).
 *
 * Calls (party-scoped server-side; no client partyId):
 *   GET  /services/embedded/api/documents
 *   GET  /services/embedded/api/documents/{id}
 *   GET  /services/embedded/api/documents/{id}/file   (blob)
 *   POST /services/embedded/api/documents             (multipart {file, kind})
 *
 * Rides the Keycloak bearer (`authInterceptor`). There is NO SCA on document upload
 * (PR-03 is not SCA-marked). Rule 07 / Rule 10 — no Mock.
 */
@Injectable()
export class SmeDocumentsRestService implements SmeDocumentsApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.DOCUMENTS;

  list(): Observable<DocumentVaultRow[]> {
    return this.http.get<DocumentVaultRow[]>(this.base);
  }

  getMetadata(id: number): Observable<DocumentVaultRow> {
    return this.http.get<DocumentVaultRow>(`${this.base}/${id}`);
  }

  upload(file: File, kind: string): Observable<DocumentUploadResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('kind', kind);
    // Let the browser set the multipart boundary; do NOT set Content-Type manually.
    return this.http.post<DocumentUploadResult>(this.base, form);
  }

  download(id: number): Observable<Blob> {
    return this.http.get(`${this.base}/${id}/file`, { responseType: 'blob' });
  }

  listForLinkedEntity(type: string, id: number): Observable<DocumentVaultRow[]> {
    return this.http.get<DocumentVaultRow[]>(`${this.base}/linked/${type}/${id}`);
  }
}
